import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNotification } from "@/context/NotificationContext";
import { KPI_CONFIG } from "@/modules/hub/features/MemberDashboard/config/kpiConfig";
import { fetchAccountMappings } from "../api/fetchAccountMappings/fetchAccountMappingsApi";
import { AccountMappingRow } from "../api/fetchAccountMappings/types";
import { fetchLedgerAccounts } from "../api/fetchLedgerAccounts/fetchLedgerAccountsApi";
import { LedgerAccount } from "../api/fetchLedgerAccounts/types";
import { saveAccountMappings } from "../api/saveAccountMappings/saveAccountMappingsApi";
import { SaveAccountMappingRow } from "../api/saveAccountMappings/types";
import { autoMapAccounts } from "../api/autoMapAccounts/autoMapAccountsApi";
import {
  LEDGER_ACCOUNT_DESCRIPTIONS,
  LedgerRowState,
  LedgerViewTab,
  isLikelySubtypeMatch,
  ledgerRowTab,
  REQUIRED_GROUP_COPY,
} from "../config/accountMappingDisplay";

/** One CertainPath account and the member's accounts sitting in it. */
export interface LedgerAccountRow {
  ledgerAccount: LedgerAccount;
  accounts: AccountMappingRow[];
  pending: AccountMappingRow[];
  state: LedgerRowState;
  /** What belongs in this account. Only the operating expense rows carry one. */
  description?: string;
  /**
   * How this row relates to the dashboard, as labelled clauses — what cannot
   * compute without it, then what merely reads it.
   *
   * Labelled because a bare card name states no relationship: "Retained
   * earnings" under a row called Retained Earnings & Net Income could be a
   * description, a category or a card, and a member had no way to tell which.
   */
  kpiRelations: KpiRelation[];
  /**
   * Display names of the KPIs that sum this account without naming it. Carried
   * separately from `kpiRelations` because the empty-row note names one card
   * rather than reading the whole line back.
   */
  summedByKpiNames: string[];
  /**
   * Retired accounts that held a balance, are mapped nowhere, and look like they
   * belong here. The suggestion sits on the row rather than in a banner so the
   * member meets it where the decision is made — and the row already says which
   * KPIs read it, which is the context a banner could not carry.
   */
  unreachedCandidates: AccountMappingRow[];
}

export interface KpiRelation {
  label: string;
  kpis: KpiReference[];
}

/** A KPI card, carrying the code so the row can link to it. */
export interface KpiReference {
  code: string;
  name: string;
}

/**
 * KPI codes to the names the dashboard shows. A code the dashboard does not
 * carry copy for is dropped rather than printed raw — the backend can name a
 * KPI the frontend has not shipped a card for yet.
 */
const kpiReferencesFor = (kpiCodes: string[]): KpiReference[] =>
  kpiCodes
    .map((code) => {
      const name = KPI_CONFIG[code]?.name;

      return name === undefined ? undefined : { code, name };
    })
    .filter((kpi): kpi is KpiReference => kpi !== undefined);

/**
 * The two relationships, in the order that matters: what the row is needed for
 * before what it merely feeds.
 *
 * Each renders on its own line, so both labels stay capitalised. An earlier cut
 * ran them together as one sentence — "Needed for Field labor · counted in Gross
 * margin, Net profit" — which read as an unbroken run of grey and buried the
 * second half, the one saying this row changes a figure without being required.
 */
const kpiRelationsFor = (
  neededBy: KpiReference[],
  summedBy: KpiReference[],
): KpiRelation[] => {
  const relations: KpiRelation[] = [];

  if (neededBy.length > 0) {
    relations.push({ label: "Needed for", kpis: neededBy });
  }

  if (summedBy.length > 0) {
    relations.push({ label: "Counted in", kpis: summedBy });
  }

  return relations;
};

export interface LedgerAccountSection {
  typeLabel: string;
  rows: LedgerAccountRow[];
  /** Shown when every account in one of this section's required groups is empty. */
  warnings: string[];
}

const accountCount = (count: number): string =>
  count === 1 ? "1 account" : `${count} accounts`;

/**
 * What the toast says after an auto-map run.
 *
 * Auto-map only ever proposes, so the notification asks for the review rather
 * than reporting a success and stopping. Both counts come back from the
 * endpoint, which already reports them.
 *
 * "Success" is the house title; a run that matched nothing did not fail, but
 * saying "Success" over it would be a stretch, so it says what happened.
 */
const autoMapNotification = (
  mappedCount: number,
  unresolvedCount: number,
): { title: string; description: string } => {
  if (mappedCount === 0) {
    return {
      title: "Auto-map finished",
      description:
        unresolvedCount === 0
          ? "Every account is already mapped."
          : `No accounts could be matched automatically. ${accountCount(unresolvedCount)} still ${unresolvedCount === 1 ? "needs" : "need"} to be mapped.`,
    };
  }

  const mapped = `${accountCount(mappedCount)} ${mappedCount === 1 ? "was" : "were"} successfully mapped. Please review and confirm.`;

  return {
    title: "Success",
    description:
      unresolvedCount === 0
        ? mapped
        : `${mapped} ${accountCount(unresolvedCount)} could not be matched.`,
  };
};

export const useAccountMappingSection = (companyUuid?: string) => {
  const { showNotification } = useNotification();
  const [accounts, setAccounts] = useState<AccountMappingRow[]>([]);
  const [ledgerAccounts, setLedgerAccounts] = useState<LedgerAccount[]>([]);
  const [syncedAt, setSyncedAt] = useState<string | null>(null);
  // Two independent requests, and the section is meaningless until both have
  // landed: with the catalog missing every count reads zero and the page
  // asserts "nothing needs attention", which is false rather than merely
  // incomplete.
  const [isLoadingMappings, setIsLoadingMappings] = useState(true);
  const [isLoadingLedgerAccounts, setIsLoadingLedgerAccounts] = useState(true);
  const isLoading = isLoadingMappings || isLoadingLedgerAccounts;
  const [isSaving, setIsSaving] = useState(false);
  const [isAutoMapping, setIsAutoMapping] = useState(false);
  const [activeTab, setActiveTab] = useState<LedgerViewTab>("review");
  const hasChosenTab = useRef(false);
  const [searchQuery, setSearchQuery] = useState("");
  /**
   * Whether retired accounts are offered in the pickers. Page-level rather than
   * per-drawer: working through retired accounts is a mode a member is in across
   * every account they open, and the notice that sends them there has to turn it
   * on before any drawer exists.
   */
  const [showInactive, setShowInactive] = useState(false);
  const [savingAccountIds, setSavingAccountIds] = useState<Set<string>>(
    new Set(),
  );

  const abortControllerRef = useRef<AbortController | null>(null);

  const cancelPendingRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const createSignal = useCallback(() => {
    cancelPendingRequest();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    return controller.signal;
  }, [cancelPendingRequest]);

  /**
   * `isBackground` keeps the list on screen while it refreshes. Only the first
   * load has nothing to show and earns the full-page spinner; a refresh after a
   * save would otherwise unmount the section and throw away scroll position
   * for a round trip the member did not ask to watch.
   */
  const loadMappings = useCallback(
    (isBackground = false) => {
      if (!companyUuid) {
        setIsLoadingMappings(false);
        return Promise.resolve();
      }

      const signal = createSignal();

      if (!isBackground) {
        setIsLoadingMappings(true);
      }

      return fetchAccountMappings(companyUuid, signal)
        .then((response) => {
          setAccounts(response.data.accounts);
          setSyncedAt(response.data.syncedAt);
        })
        .finally(() => {
          if (!isBackground) {
            setIsLoadingMappings(false);
          }
        });
    },
    [companyUuid, createSignal],
  );

  useEffect(() => {
    fetchLedgerAccounts()
      .then((response) => {
        setLedgerAccounts(response.data);
      })
      .finally(() => setIsLoadingLedgerAccounts(false));
  }, []);

  useEffect(() => {
    loadMappings();
    return cancelPendingRequest;
  }, [loadMappings, cancelPendingRequest]);

  const persist = useCallback(
    (rows: SaveAccountMappingRow[]) => {
      if (!companyUuid || rows.length === 0) {
        return;
      }

      const savingIds = rows.map((row) => row.qboAccountId);

      setIsSaving(true);
      // Added rather than replaced: two accounts changed in quick succession
      // are both in flight, and the first to land must not clear the second.
      setSavingAccountIds((current) => new Set([...current, ...savingIds]));

      saveAccountMappings(companyUuid, { mappings: rows }).finally(() => {
        // Refresh after a failed save too — an optimistic row that never
        // persisted has to snap back to what the server actually holds.
        loadMappings(true).finally(() => {
          setIsSaving(false);
          setSavingAccountIds((current) => {
            const next = new Set(current);
            savingIds.forEach((id) => next.delete(id));
            return next;
          });
        });
      });
    },
    [companyUuid, loadMappings],
  );

  /**
   * Shows the member's choice the moment they make it, so a chip does not wait
   * out the round trip before appearing where they put it. Only what the row
   * can derive locally is set — `status` and `flags` are the server's to decide
   * and arrive with the refresh rather than being guessed at here.
   */
  const applyLocally = useCallback(
    (qboAccountIds: string[], ledgerAccountCode: string | null) => {
      setAccounts((current) =>
        current.map((account) => {
          if (!qboAccountIds.includes(account.qboAccountId)) {
            return account;
          }

          if (ledgerAccountCode === null) {
            return { ...account, mapping: null };
          }

          const ledgerAccount = ledgerAccounts.find(
            (candidate) => candidate.code === ledgerAccountCode,
          );

          return {
            ...account,
            mapping: {
              ledgerAccountCode,
              ledgerAccountLabel: ledgerAccount?.label ?? "",
              suggestedLedgerAccountCode:
                account.mapping?.suggestedLedgerAccountCode ?? null,
              source: "manual",
              confidence: null,
              isConfirmed: true,
              isCrossType:
                ledgerAccount !== undefined &&
                ledgerAccount.ledgerAccountType !== account.ledgerAccountType,
            },
          };
        }),
      );
    },
    [ledgerAccounts],
  );

  /** Puts one or more QuickBooks accounts into a CertainPath account. */
  const assignAccounts = useCallback(
    (qboAccountIds: string[], ledgerAccountCode: string) => {
      applyLocally(qboAccountIds, ledgerAccountCode);
      persist(
        qboAccountIds.map((qboAccountId) => ({
          qboAccountId,
          ledgerAccountCode,
          isConfirmed: true,
        })),
      );
    },
    [applyLocally, persist],
  );

  const unassignAccount = useCallback(
    (qboAccountId: string) => {
      applyLocally([qboAccountId], null);
      persist([{ qboAccountId, ledgerAccountCode: null, isConfirmed: false }]);
    },
    [applyLocally, persist],
  );

  /** Accepts suggestions the member has not agreed to yet. */
  const confirmAccounts = useCallback(
    (qboAccountIds: string[]) => {
      persist(
        accounts
          .filter(
            (account) =>
              qboAccountIds.includes(account.qboAccountId) && account.mapping,
          )
          .map((account) => ({
            qboAccountId: account.qboAccountId,
            ledgerAccountCode: account.mapping?.ledgerAccountCode ?? null,
            isConfirmed: true,
          })),
      );
    },
    [accounts, persist],
  );

  const runAutoMap = useCallback(() => {
    if (!companyUuid) {
      return;
    }

    setIsAutoMapping(true);

    autoMapAccounts(companyUuid)
      .then(({ data }) => {
        const { title, description } = autoMapNotification(
          data.rulesCount + data.aiCount,
          data.unresolvedCount,
        );

        showNotification(title, description, "success");
      })
      .finally(() => {
        loadMappings(true).finally(() => setIsAutoMapping(false));
      });
  }, [companyUuid, loadMappings, showNotification]);

  const accountsByLedgerCode = useMemo(() => {
    const grouped = new Map<string, AccountMappingRow[]>();

    accounts.forEach((account) => {
      const code = account.mapping?.ledgerAccountCode;

      if (!code) {
        return;
      }

      grouped.set(code, [...(grouped.get(code) ?? []), account]);
    });

    return grouped;
  }, [accounts]);

  /**
   * What auto-map has to work with. Retired accounts are left out: a member has
   * already decided about them, so a machine's guess only costs them a review.
   * This drives both the button's count and the ids it sends.
   */
  const unplacedAccounts = useMemo(
    () =>
      accounts.filter(
        (account) => account.mapping === null && account.isActive,
      ),
    [accounts],
  );

  /**
   * Money in the member's history that reaches no KPI.
   *
   * A retired account that held a balance in the dashboard's window and was
   * never mapped is the one thing on this page nothing else reports: auto-map
   * skips retired accounts, the unplaced count excludes them, and the review
   * band only knows about mappings that already exist. Left alone, the member's
   * earlier months are understated and the chart shows money appearing where it
   * was flat.
   */
  const unreachedBalanceAccounts = useMemo(
    () =>
      accounts.filter(
        (account) =>
          !account.isActive &&
          account.mapping === null &&
          account.hasRecentBalance,
      ),
    [accounts],
  );

  const rows = useMemo(
    (): LedgerAccountRow[] =>
      ledgerAccounts.map((ledgerAccount) => {
        const summedBy = kpiReferencesFor(ledgerAccount.summedByKPICodes);
        const mapped = accountsByLedgerCode.get(ledgerAccount.code) ?? [];
        const pending = mapped.filter(
          (account) => account.mapping?.isConfirmed === false,
        );

        let state: LedgerRowState = "empty";

        if (pending.length > 0) {
          state = "pending";
        } else if (mapped.length > 0) {
          state = "filled";
        } else if (ledgerAccount.isRequired) {
          state = "gap";
        }

        return {
          ledgerAccount,
          accounts: mapped,
          pending,
          state,
          description: LEDGER_ACCOUNT_DESCRIPTIONS[ledgerAccount.code],
          kpiRelations: kpiRelationsFor(
            kpiReferencesFor(ledgerAccount.requiredByKPICodes),
            summedBy,
          ),
          summedByKpiNames: summedBy.map((kpi) => kpi.name),
          unreachedCandidates: unreachedBalanceAccounts.filter((account) =>
            isLikelySubtypeMatch(
              account.accountSubType,
              ledgerAccount.qboSubtypeMatches,
            ),
          ),
        };
      }),
    [ledgerAccounts, accountsByLedgerCode, unreachedBalanceAccounts],
  );

  /**
   * The remainder: balance-holding retired accounts whose subtype matches no
   * CertainPath account at all, so no row can suggest them. Without this they
   * would appear nowhere, which is the failure the whole work item is about.
   */
  const unsuggestedBalanceAccounts = useMemo(
    () =>
      unreachedBalanceAccounts.filter(
        (account) =>
          !ledgerAccounts.some((ledgerAccount) =>
            isLikelySubtypeMatch(
              account.accountSubType,
              ledgerAccount.qboSubtypeMatches,
            ),
          ),
      ),
    [unreachedBalanceAccounts, ledgerAccounts],
  );

  /**
   * A group warning fires only when every account satisfying it is empty — one
   * member is enough, which is the whole reason these are groups rather than
   * required rows.
   */
  const warningsByTypeLabel = useMemo(() => {
    const warnings = new Map<string, string[]>();
    const groupKeys = new Set(
      ledgerAccounts.flatMap(
        (ledgerAccount) => ledgerAccount.requiredGroupKeys,
      ),
    );

    groupKeys.forEach((groupKey) => {
      const members = ledgerAccounts.filter((ledgerAccount) =>
        ledgerAccount.requiredGroupKeys.includes(groupKey),
      );

      const isSatisfied = members.some(
        (member) => (accountsByLedgerCode.get(member.code) ?? []).length > 0,
      );
      const copy = REQUIRED_GROUP_COPY[groupKey];

      if (isSatisfied || !copy || members.length === 0) {
        return;
      }

      const typeLabel = members[0].ledgerAccountTypeLabel;
      warnings.set(typeLabel, [...(warnings.get(typeLabel) ?? []), copy]);
    });

    return warnings;
  }, [ledgerAccounts, accountsByLedgerCode]);

  const visibleSections = useMemo((): LedgerAccountSection[] => {
    const query = searchQuery.trim().toLowerCase();

    const matchesTab = (row: LedgerAccountRow): boolean =>
      activeTab === "all" ||
      ledgerRowTab(row.accounts.length, row.pending.length) === activeTab;

    const matchesQuery = (row: LedgerAccountRow): boolean =>
      query === "" ||
      row.ledgerAccount.label.toLowerCase().includes(query) ||
      row.accounts.some(
        (account) =>
          account.name.toLowerCase().includes(query) ||
          (account.accountNumber ?? "").toLowerCase().includes(query),
      );

    const sections: LedgerAccountSection[] = [];

    rows.forEach((row) => {
      if (!matchesTab(row) || !matchesQuery(row)) {
        return;
      }

      const typeLabel = row.ledgerAccount.ledgerAccountTypeLabel;
      const section = sections.find(
        (candidate) => candidate.typeLabel === typeLabel,
      );

      if (section) {
        section.rows.push(row);
        return;
      }

      sections.push({
        typeLabel,
        rows: [row],
        warnings: warningsByTypeLabel.get(typeLabel) ?? [],
      });
    });

    return sections;
  }, [rows, activeTab, searchQuery, warningsByTypeLabel]);

  const tabCounts = useMemo(() => {
    const tally = { all: rows.length, unmapped: 0, review: 0, confirmed: 0 };

    rows.forEach((row) => {
      tally[ledgerRowTab(row.accounts.length, row.pending.length)] += 1;
    });

    return tally;
  }, [rows]);

  /**
   * Lands on the work rather than on a view. After an auto-map that is almost
   * always the review queue; once it is empty, whatever is still unmapped.
   *
   * Only steers the initial view — `hasChosenTab` keeps it from yanking the
   * member elsewhere the moment they clear the last row of the tab they are on.
   */
  useEffect(() => {
    if (hasChosenTab.current || isLoading) {
      return;
    }

    hasChosenTab.current = true;

    if (tabCounts.review > 0) {
      setActiveTab("review");
      return;
    }

    setActiveTab(tabCounts.unmapped > 0 ? "unmapped" : "all");
  }, [isLoading, tabCounts]);

  return {
    accounts,
    ledgerAccounts,
    rows,
    visibleSections,
    unplacedAccounts,
    unreachedBalanceAccounts,
    unsuggestedBalanceAccounts,
    showInactive,
    setShowInactive,
    tabCounts,
    syncedAt,
    isLoading,
    isSaving,
    savingAccountIds,
    isAutoMapping,
    activeTab,
    setActiveTab,
    searchQuery,
    setSearchQuery,
    assignAccounts,
    unassignAccount,
    confirmAccounts,
    runAutoMap,
    refresh: loadMappings,
  };
};
